/**
 * Task Import Service
 * API client for task import/export operations.
 *
 * NOTE: Currently using mock responses for frontend-first development.
 * Replace mock implementations with actual API calls when backend is ready.
 */

import HttpService from '@/utils/httpService';
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';
import userService from '@/features/User/service/user.service';

const LOG_PREFIX = '[TaskImportService]';

// =============================================================================
// Configuration
// =============================================================================

const USE_MOCKS = false; // Backend API is now ready

// Mock delay to simulate network latency
const MOCK_DELAY = 500;

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// =============================================================================
// Mock Data
// =============================================================================

const MOCK_TASK_TYPES = [
  {
    id: 'tt-001',
    name: 'Safety Inspections',
    description: 'Track safety inspection tasks',
    taskCount: 230, // Number of tasks in this board
    statuses: [
      { id: 's1', name: 'Not Started', color: '#9E9E9E', order: 0 },
      { id: 's2', name: 'In Progress', color: '#2196F3', order: 1 },
      { id: 's3', name: 'Review', color: '#FF9800', order: 2 },
      { id: 's4', name: 'Completed', color: '#4CAF50', order: 3 },
    ],
    customFields: [
      { id: 'cf1', name: 'Inspector', type: 1, section: 'Details' },
      { id: 'cf2', name: 'Location', type: 1, section: 'Details' },
      { id: 'cf3', name: 'Risk Level', type: 6, section: 'Assessment', options: ['Low', 'Medium', 'High', 'Critical'] },
    ],
  },
  {
    id: 'tt-002',
    name: 'Maintenance Tasks',
    description: 'Equipment maintenance tracking',
    taskCount: 85, // Number of tasks in this board
    statuses: [
      { id: 's1', name: 'Pending', color: '#9E9E9E', order: 0 },
      { id: 's2', name: 'Assigned', color: '#2196F3', order: 1 },
      { id: 's3', name: 'Done', color: '#4CAF50', order: 2 },
    ],
    customFields: [
      { id: 'cf4', name: 'Equipment ID', type: 1, section: 'Equipment' },
      { id: 'cf5', name: 'Cost Estimate', type: 2, section: 'Financial' },
      { id: 'cf6', name: 'Warranty Valid', type: 3, section: 'Equipment' },
    ],
  },
  {
    id: 'tt-003',
    name: 'Project Milestones',
    description: 'Track project deliverables',
    taskCount: 42, // Number of tasks in this board
    statuses: [
      { id: 's1', name: 'Planned', color: '#9E9E9E', order: 0 },
      { id: 's2', name: 'Active', color: '#2196F3', order: 1 },
      { id: 's3', name: 'Blocked', color: '#F44336', order: 2 },
      { id: 's4', name: 'Achieved', color: '#4CAF50', order: 3 },
    ],
    customFields: [
      { id: 'cf7', name: 'Phase', type: 6, section: 'Project', options: ['Phase 1', 'Phase 2', 'Phase 3'] },
      { id: 'cf8', name: 'Budget', type: 2, section: 'Financial' },
    ],
  },
];

const MOCK_IMPORT_HISTORY = [
  {
    id: 'log-001',
    taskTypeId: 'tt-001',
    sourceFileName: 'inspections-q4-2025.xlsx',
    importType: 'Import',
    totalRecords: 150,
    createdCount: 145,
    updatedCount: 5,
    skippedCount: 0,
    status: 'Success',
    createDate: '2025-12-15T10:30:00Z',
    createdBy: 'John Doe',
  },
  {
    id: 'log-002',
    taskTypeId: 'tt-001',
    sourceFileName: 'inspections-november.xlsx',
    importType: 'Import',
    totalRecords: 80,
    createdCount: 75,
    updatedCount: 3,
    skippedCount: 2,
    status: 'PartialSuccess',
    createDate: '2025-11-20T14:45:00Z',
    createdBy: 'Jane Smith',
  },
];

// =============================================================================
// Service Methods
// =============================================================================

const taskImportService = {
  /**
   * Get full TaskType details by ID.
   * Includes sections, fields, statuses - needed for field mapping.
   * @param {string} taskTypeId - TaskType ID
   * @returns {Promise<Object>} Full TaskType with schema
   */
  async getTaskTypeById(taskTypeId) {
    debugLog(LOG_PREFIX, 'Fetching full TaskType details', { taskTypeId });

    const response = await HttpService.get({
      url: `api/TaskTypes/${taskTypeId}`,
    });
    return response.data || response;
  },

  /**
   * Get tasks for a TaskBoard with server-side pagination.
   * @param {Object} params - { taskTypeId, page, itemsPerPage, sortBy, sortDesc }
   * @returns {Promise<Object>} { items: Task[], total: number }
   */
  async getTasks({ taskTypeId, page = 1, itemsPerPage = 15, sortBy = 'friendlyId', sortDesc = true }) {
    debugLog(LOG_PREFIX, 'Fetching tasks', { taskTypeId, page, itemsPerPage, sortBy, sortDesc });

    const response = await HttpService.get({
      url: 'api/Tasks',
      params: {
        TypeId: taskTypeId,
        Page: page,
        ItemsPerPage: itemsPerPage,
        SortBy: sortBy,
        SortDesc: sortDesc,
      },
    });
    return response.data || response;
  },

  /**
   * Get available TaskBoards for a scope.
   * Uses the dedicated ForImport endpoint that returns lightweight data with task counts.
   * Supports both Facility and FacilityGroup filtering.
   * @param {Object} params - { scopeType, targetId }
   * @returns {Promise<Array>} List of TaskType objects with taskCount
   */
  async getTaskTypes({ scopeType, targetId }) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);
      debugLog(LOG_PREFIX, 'Mock: Returning TaskTypes', { scopeType, targetId });
      return [...MOCK_TASK_TYPES];
    }

    // Use the new ForImport endpoint - supports both Facility and FacilityGroup
    // Returns lightweight response with task counts
    const params = {};
    if (scopeType && targetId) {
      params.scopeType = scopeType;
      params.targetId = targetId;
    }

    debugLog(LOG_PREFIX, 'Fetching TaskTypes for import', { scopeType, targetId });

    const response = await HttpService.get({
      url: 'api/TaskTypes/ForImport',
      params,
    });
    return response.data || response;
  },

  /**
   * Check for duplicate tasks by title.
   * @param {Object} params - { taskTypeId, titles }
   * @returns {Promise<Object>} Duplicate check result
   */
  async checkDuplicates({ taskTypeId, titles }) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);
      // Mock: 10% of titles are duplicates
      const duplicateCount = Math.floor(titles.length * 0.1);
      const duplicateTitles = titles.slice(0, duplicateCount);

      const result = {
        totalChecked: titles.length,
        duplicatesFound: duplicateCount,
        duplicateTitles,
      };

      debugLog(LOG_PREFIX, 'Mock: Duplicate check result', result);
      return result;
    }

    const response = await HttpService.post({
      url: 'api/task-imports/check-duplicates',
      data: { taskTypeId, titles },
    });
    return response.data || response;
  },

  /**
   * Import tasks to an existing TaskBoard.
   * @param {Object} request - Import request with records
   * @returns {Promise<Object>} Import result
   */
  async importTasks(request) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY * 2);

      const totalRecords = request.records.length;
      const failedCount = Math.floor(totalRecords * 0.02); // 2% failure rate
      const duplicateCount = Math.floor(totalRecords * 0.1); // 10% are updates
      const createdCount = totalRecords - failedCount - duplicateCount;
      const updatedCount = duplicateCount;

      const result = {
        status: failedCount > 0 ? 'PartialSuccess' : 'Success',
        totalRecords,
        createdCount,
        updatedCount,
        skippedCount: 0,
        failedCount,
        errors: failedCount > 0 ? [
          { row: 5, field: 'dueDate', message: 'Invalid date format' },
          { row: 12, field: 'progress', message: 'Value must be between 0 and 100' },
        ].slice(0, failedCount) : [],
        taskTypeId: request.taskTypeId,
        importLogId: `log-${Date.now()}`,
      };

      debugLog(LOG_PREFIX, 'Mock: Import result', result);
      return result;
    }

    const response = await HttpService.post({
      url: 'api/task-imports',
      data: request,
    });
    return response.data || response;
  },

  /**
   * Create a new TaskBoard and import tasks.
   * @param {Object} request - Import request with schema and records
   * @returns {Promise<Object>} Import result with new TaskType info
   */
  async createBoardAndImport(request) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY * 3);

      const totalRecords = request.records.length;
      const failedCount = Math.floor(totalRecords * 0.02);
      const createdCount = totalRecords - failedCount;

      const result = {
        status: failedCount > 0 ? 'PartialSuccess' : 'Success',
        totalRecords,
        createdCount,
        updatedCount: 0,
        skippedCount: 0,
        failedCount,
        errors: [],
        taskTypeId: `tt-new-${Date.now()}`,
        taskTypeName: request.newTaskTypeName,
        importLogId: `log-${Date.now()}`,
      };

      debugLog(LOG_PREFIX, 'Mock: Create and import result', result);
      return result;
    }

    const response = await HttpService.post({
      url: 'api/task-imports/create-board-and-import',
      data: request,
    });
    return response.data || response;
  },

  /**
   * Export tasks from a TaskBoard to CSV.
   * @param {string} taskTypeId - TaskBoard ID
   * @param {Object} options - Export options
   * @returns {Promise<Blob>} CSV file blob
   */
  async exportTasks(taskTypeId, options = {}) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);

      // Generate mock CSV content
      const csvContent = [
        'Task Title,Description,Status,Progress,Due Date,Priority',
        'Safety Inspection #1,Monthly inspection of Area A,Completed,100,2025-12-01,High',
        'Maintenance Review,Quarterly equipment review,In Progress,50,2025-12-15,Medium',
        'Documentation Update,Update SOPs,Not Started,0,2025-12-31,Low',
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });

      debugLog(LOG_PREFIX, 'Mock: Export generated', { taskTypeId, size: blob.size });
      return blob;
    }

    const response = await HttpService.get({
      url: `api/task-imports/export/${taskTypeId}`,
      params: options,
      responseType: 'blob',
    });
    return response.data || response;
  },

  /**
   * Get import history for a TaskBoard.
   * @param {string} taskTypeId - TaskBoard ID
   * @param {Object} pagination - { page, itemsPerPage }
   * @returns {Promise<Object>} { items, total }
   */
  async getImportHistory(taskTypeId, pagination = {}) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);

      const filtered = MOCK_IMPORT_HISTORY.filter(h => h.taskTypeId === taskTypeId);

      const result = {
        items: filtered,
        total: filtered.length,
      };

      debugLog(LOG_PREFIX, 'Mock: Import history', result);
      return result;
    }

    const response = await HttpService.get({
      url: `api/task-imports/history/${taskTypeId}`,
      params: pagination,
    });
    return response.data || response;
  },

  /**
   * Get all import history with filtering and pagination.
   * Used for the Import History Sidebar.
   * @param {Object} filters - { page, itemsPerPage, status, fromDate, toDate, facilityId, facilityGroupId }
   * @returns {Promise<Object>} { items, total }
   */
  async getAllImportHistory(filters = {}) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);

      let items = [...MOCK_IMPORT_HISTORY];

      // Apply status filter
      if (filters.status) {
        items = items.filter(h => h.status === filters.status);
      }

      const result = {
        items,
        total: items.length,
      };

      debugLog(LOG_PREFIX, 'Mock: All import history', result);
      return result;
    }

    const params = {
      page: filters.page || 1,
      itemsPerPage: filters.itemsPerPage || 20,
    };

    // Add optional filters
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.facilityId) params.facilityId = filters.facilityId;
    if (filters.facilityGroupId) params.facilityGroupId = filters.facilityGroupId;

    debugLog(LOG_PREFIX, 'Fetching all import history', params);

    const response = await HttpService.get({
      url: 'api/task-imports/history',
      params,
    });
    return response.data || response;
  },

  /**
   * Delete a specific import log entry.
   * @param {string} logId - Import log ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteImportLog(logId) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);
      debugLog(LOG_PREFIX, 'Mock: Deleted import log', { logId });
      return true;
    }

    debugLog(LOG_PREFIX, 'Deleting import log', { logId });

    await HttpService.delete({
      url: `api/task-imports/history/${logId}`,
    });
    return true;
  },

  /**
   * Clear old import logs.
   * @param {Object} params - { olderThanDays }
   * @returns {Promise<number>} Number of logs deleted
   */
  async clearOldImportLogs(params = {}) {
    if (USE_MOCKS) {
      await delay(MOCK_DELAY);
      debugLog(LOG_PREFIX, 'Mock: Cleared old logs', params);
      return 5;
    }

    debugLog(LOG_PREFIX, 'Clearing old import logs', params);

    const response = await HttpService.delete({
      url: 'api/task-imports/history/clear',
      params,
    });
    return response.data || response;
  },

  /**
   * Resolve user emails to user IDs.
   * Uses lenient validation - returns null for unresolved emails.
   * @param {string[]} emails - Array of email addresses to resolve
   * @returns {Promise<Object>} Object with:
   *   - mappings: { email: userId | null }
   *   - resolved: number of successfully resolved emails
   *   - unresolved: array of emails that couldn't be resolved
   */
  async resolveUserEmails(emails) {
    if (!emails || emails.length === 0) {
      return { mappings: {}, resolved: 0, unresolved: [] };
    }

    // Get unique emails (case-insensitive)
    const uniqueEmails = [...new Set(emails.map(e => e?.toLowerCase().trim()).filter(Boolean))];

    debugLog(LOG_PREFIX, 'Resolving user emails', { count: uniqueEmails.length });

    try {
      // Fetch all users using the typeHead endpoint
      const response = await userService.typeHead();
      const users = response.data || response || [];

      // Create email-to-user mapping (case-insensitive)
      const emailToUser = {};
      users.forEach(user => {
        if (user.email) {
          emailToUser[user.email.toLowerCase().trim()] = user;
        }
      });

      // Resolve each email
      const mappings = {};
      const unresolved = [];

      uniqueEmails.forEach(email => {
        const user = emailToUser[email];
        if (user) {
          mappings[email] = {
            userId: user.id,
            displayName: user.displayName || user.name || user.email,
            email: user.email,
          };
        } else {
          mappings[email] = null;
          unresolved.push(email);
        }
      });

      const result = {
        mappings,
        resolved: uniqueEmails.length - unresolved.length,
        unresolved,
      };

      debugLog(LOG_PREFIX, 'User email resolution complete', {
        total: uniqueEmails.length,
        resolved: result.resolved,
        unresolved: unresolved.length,
      });

      if (unresolved.length > 0) {
        debugWarn(LOG_PREFIX, 'Some emails could not be resolved', unresolved);
      }

      return result;
    } catch (err) {
      debugWarn(LOG_PREFIX, 'Failed to resolve user emails', err);
      // Return all as unresolved on error
      const mappings = {};
      uniqueEmails.forEach(email => {
        mappings[email] = null;
      });
      return {
        mappings,
        resolved: 0,
        unresolved: uniqueEmails,
      };
    }
  },
};

export default taskImportService;
